import { Injectable } from '@angular/core';
import { v4 } from 'uuid';
import { Case, Requirement } from '../models/case';
import { Claim } from '../models/claim';
import { DatabaseService } from './base/database.service';
import { UserService } from './user.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Action, EnrichedAction } from '../models/action.model';
import { User } from '../models/user.model';
import {
  sortCaseActions,
  findDoneActionForCaseAction,
  CaseActionWithRelations,
  ActionPrerequisite,
  DoneAction,
} from '../utils/sort-case-actions.util';
import { WithdrawFormData } from 'src/app/modules/client/shared/simple/withdraw-form-modal/withdraw-form-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { CaseWithdrawalConfig, WithdrawalFlowConfig } from '../models/withdrawal-config';
import { getValue } from 'src/app/shared/utils/hideif';

export interface ClaimOrigination {
  button: string;
  url_params: {
    url: string;
    path: string;
    pathParams: string[] | null;
    querystring: string | null;
    querystringParams: Record<string, unknown> | null;
  };
}

/**
 * Builds attributes.origination for claims created directly in the Client Portal
 * (self-service multi-claim), mirroring the shape landing pages send for origination.
 */
export function buildClaimOrigination(button: string): ClaimOrigination {
  const pathPart = window.location.pathname;
  const qsPart = window.location.search ? window.location.search.substring(1) : null;
  const pathSegments = pathPart.split('/').filter(Boolean);
  const querystringParams: Record<string, unknown> = {};
  if (qsPart) {
    new URLSearchParams(qsPart).forEach((val, key) => {
      const num = Number(val);
      querystringParams[key] = !isNaN(num) && val !== '' ? num : val;
    });
  }
  return {
    button,
    url_params: {
      url: window.location.href,
      path: pathPart || '/',
      pathParams: pathSegments.length > 0 ? pathSegments : null,
      querystring: qsPart ?? null,
      querystringParams: Object.keys(querystringParams).length > 0 ? querystringParams : null,
    },
  };
}

@Injectable({ providedIn: 'root' })
export class ClaimsService extends DatabaseService<Claim> {
  constructor(
    private us: UserService,
    private http: HttpClient,
    private ts: TranslateService
  ) {
    super('cp_claim');
  }

  async getMyClaimsWithCase(limit: number, page: number) {
    const user = await this.us.getCurrentUser();
    console.log('[ClaimsService] getMyClaimsWithCase User:', {
      id: user?.id,
      auth_id: user?.auth_id,
      display_name: user?.display_name
    });
    if (!user) {
      console.log('[ClaimsService] getMyClaimsWithCase - No user, returning empty array');
      return [];
    }

    const query = this._getClaimWithCaseForUserIdQuery(
      user.id!,
      page,
      limit
    ).neq('status', 'INACTIVE');
    const { data, error } = await query;

    console.log('[ClaimsService] getMyClaimsWithCase Query result:', {
      userId: user.id,
      claimsFound: data?.length ?? 0,
      error: error?.message,
      claimIds: data?.map((c: any) => c.id)
    });

    if (error) throw new Error(error.message);

    return data as unknown as ClaimWithCase[];
  }

  async getMyActiveClaimForCase(caseId: string): Promise<ClaimWithCase | null> {
    const user = await this.us.getCurrentUser();
    if (!user || !user.id || !caseId) return null;

    const { data, error } = await this.supabase
      .from('cp_claim')
      .select('*, cp_case (*), cp_claim_permission!inner(*)')
      .eq('cp_claim_permission.principal_id', user.id)
      .eq('case_id', caseId)
      .eq('status', 'ACTIVE')
      .order('created_at', { ascending: false, nullsFirst: false })
      .limit(1);

    if (error) {
      console.error('[ClaimsService] getMyActiveClaimForCase error:', error);
      throw new Error(error.message);
    }

    return (data?.[0] as unknown as ClaimWithCase) ?? null;
  }

  async getMyLatestInactiveClaim(): Promise<ClaimWithCase | null> {
    const user = await this.us.getCurrentUser();
    if (!user || !user.id) return null;

    const { data, error } = await this.supabase
      .from('cp_claim')
      .select('*, cp_case (*), cp_claim_permission!inner(*)')
      .eq('cp_claim_permission.principal_id', user.id)
      .eq('status', 'INACTIVE')
      .order('created_at', { ascending: false, nullsFirst: false })
      .limit(1);

    if (error) {
      console.error('[ClaimsService] getMyLatestInactiveClaim error:', error);
      return null;
    }

    return (data?.[0] as unknown as ClaimWithCase) ?? null;
  }

  /**
   * Case do claim já foi concluído? Definição do CPK-4171: existe um
   * cp_signed_contract com status = SIGNED para este claim.
   */
  async isClaimFinished(claimId: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from('cp_signed_contract')
      .select('id', { count: 'exact', head: true })
      .eq('claim_id', claimId)
      .eq('status', 'SIGNED');

    if (error) {
      console.warn('[ClaimsService] isClaimFinished error', { error, claimId });
      return false;
    }
    return (count ?? 0) > 0;
  }

  /**
   * True se QUALQUER claim do usuário atual já tem cp_signed_contract com
   * status = SIGNED — usado para liberar a navegação travada (nav lock) de
   * quem chegou via landing page (CPK-4172).
   */
  async hasAnyLegalPackSigned(): Promise<boolean> {
    const claims = await this.getMyClaimsWithCase(50, 0);
    for (const claim of claims) {
      if (await this.isClaimFinished(claim.id)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Claim não-INACTIVE mais recente (created_at DESC) cujo case ainda não
   * foi concluído — usado quando o login não aponta para um claim
   * específico (login direto / magic link, sem redirectTo).
   */
  async getMyMostRecentUnfinishedClaim(): Promise<ClaimWithCase | null> {
    const claims = await this.getMyClaimsWithCase(50, 0);
    for (const claim of claims) {
      if (!(await this.isClaimFinished(claim.id))) {
        return claim;
      }
    }
    return null;
  }

  async getMyInvalidatedClaim() {
    const user = await this.us.getCurrentUser();
    if (!user || !user.id) return null;

    // Busca claims INACTIVE do usuário com invalidation.isInvalidated = true
    const { data, error } = await this.supabase
      .from('cp_claim')
      .select('*, cp_claim_permission!inner(*)')
      .eq('cp_claim_permission.principal_id', user.id)
      .eq('status', 'INACTIVE')
      .limit(1);

    console.log('[ClaimsService] getMyInvalidatedClaim result:', {
      userId: user.id,
      found: data?.length ?? 0,
      error: error?.message,
      claimId: data?.[0]?.id,
      attributes: data?.[0]?.attributes
    });

    if (error || !data || data.length === 0) return null;

    // Verificar se a claim tem a estrutura de invalidation
    const claim = data[0];
    const attributes = (claim.attributes || {}) as any;

    // Aceitar tanto a estrutura nova (invalidation.isInvalidated) quanto a legada (invalidated)
    if (attributes.invalidation?.isInvalidated || attributes.invalidated) {
      return claim;
    }

    return null;
  }

  private _getClaimWithCaseForUserIdQuery(
    userId: string,
    page: number,
    limit: number
  ) {
    return this.supabase
      .from('cp_claim')
      .select('*, cp_case (*),cp_claim_permission!inner(*)')
      .eq('cp_claim_permission.principal_id', userId)
      .order('created_at', { ascending: false, nullsFirst: false })
      .range(page * limit, (page + 1) * limit - 1);
  }


  /**
   * Returns true se o claim ainda tem alguma SIGNATURE pendente — i.e., uma
   * linha em cpv_outstanding_actions cuja action é do tipo SIGNATURE e que
   * ainda não atingiu status terminal (DONE/SIGNED/SUBMITTED). Usado pelo gate
   * do welcome banner: depois que o cliente assina o Legal Pack, o banner
   * (cujo CTA é justamente entrar no fluxo de assinatura/onboarding) deixa de
   * fazer sentido — mesmo que ainda restem questionários ou KYC. Em caso de
   * erro, retorna `false` (fail-closed) para não cutucar quem já completou.
   */
  async hasOutstandingSignatureActions(claimId: string): Promise<boolean> {
    const { count, error } = await this.supabase
      .from('cpv_outstanding_actions')
      .select('action_id, cp_actions!inner(action_type)', { count: 'exact', head: true })
      .eq('claim_id', claimId)
      .eq('cp_actions.action_type', 'SIGNATURE')
      .or('done_action_id.is.null,status.not.in.(DONE,SIGNED,SUBMITTED)');

    if (error) {
      console.warn('[ClaimsService] hasOutstandingSignatureActions error', { error, claimId });
      return false;
    }
    return (count ?? 0) > 0;
  }

  async getAllClaimsForUserId(userId: string) {
    const query = this.supabase
      .from('cp_claim')
      .select('*,cp_claim_permission!inner(*)')
      .eq('cp_claim_permission.principal_id', userId);

    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return data as unknown as Claim[];
  }

  /**
   * BLF (law-firm landing) claim: origination path is set and is not the root path.
   * Uses only `attributes.origination.url_params.path` (not null/undefined, not "/").
   */
  isBlfClaim(claim: Claim): boolean {
    const attrs = claim?.attributes as Record<string, unknown> | null | undefined;
    const origination = attrs?.['origination'] as
      | { url_params?: { path?: unknown } }
      | undefined;
    const raw = origination?.url_params?.path;
    if (raw == null || typeof raw !== 'string') {
      return false;
    }
    const normalized = raw.trim();
    if (normalized === '' || normalized === '/') {
      return false;
    }
    return true;
  }

  async buildClaimantStatusMap(
    userId: string
  ): Promise<Record<string, { isClaimant: boolean; isBlfClaimant: boolean }>> {
    const claims = await this.getAllClaimsForUserId(userId);
    const map: Record<
      string,
      { isClaimant: boolean; isBlfClaimant: boolean }
    > = {};

    for (const claim of claims) {
      if (!claim.case_id || map[claim.case_id]) {
        continue;
      }
      map[claim.case_id] = {
        isClaimant: true,
        isBlfClaimant: this.isBlfClaim(claim),
      };
    }

    return map;
  }

  async getClaimWithCaseForUserId(userId: string, limit: number, page: number) {
    console.log('getClaimWithCaseForUserId', userId);
    const query = this._getClaimWithCaseForUserIdQuery(userId, page, limit);
    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return data as unknown as ClaimWithCase[];
  }

  async getUserForClaimId(claimId: string): Promise<any> {
    try {
      const { data, error } = await this.supabase
        .from('cp_claim')
        .select('*, cp_claim_permission!inner(principal_id, permission_type)')
        .eq('id', claimId);

      if (error) {
        throw new Error(error.message);
      }

      const userIds = data.flatMap((claim) =>
        claim.cp_claim_permission.map((permission) => permission.principal_id)
      );

      const uniqueUserIds = [...new Set(userIds)].filter((id) => id !== null);

      // Fetch user details based on the userIds
      const { data: users, error: userError } = await this.supabase
        .from('cp_user') // Replace 'users' with your actual user table name
        .select('*')
        .in('id', uniqueUserIds);

      if (userError) {
        throw new Error(userError.message);
      }

      return {
        users: users as unknown as User[],
        claims: data as unknown as Claim[],
      };
    } catch (error) {
      console.error('Error fetching user for claim:', error);
      throw error;
    }
  }

  async getClaimWithCase(claimId: string) {
    const query = this.supabase
      .from('cp_claim')
      .select('*, attributes, case_id, cp_case (*)')
      .eq('id', claimId);
    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return data[0] as unknown as ClaimWithCase;
  }

  /** Emissões da claim em cp_issued_claim (CPK-5205), a mais recente primeiro. */
  async getIssuedClaimsByClaim(claimId: string): Promise<IssuedClaimRow[]> {
    const { data, error } = await this.supabase
      .from('cp_issued_claim')
      .select('id, status, result, issued_date, created_at, claim_form_name')
      .eq('claim_id', claimId)
      .order('issued_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);

    return (data ?? []) as IssuedClaimRow[];
  }

  async getRequirementsListForClaim(
    cwc: ClaimWithCase
  ): Promise<ClaimRequirement[]> {
    if (!cwc.cp_case.requirements) {
      return [];
    } else {
      const r: ClaimRequirement[] = [];

      for (let i = 0; i < cwc.cp_case.requirements.length; i++) {
        const element = cwc.cp_case.requirements[i];
        const reqStatus = await this.checkRequirementStatus(element, cwc);
        r.push({
          claimId: cwc.id,
          requirement: element,
          status: reqStatus.status,
          data: reqStatus.data,
        });
      }

      return r;
    }
  }

  async getRequirementsListForClaimV2(claim_id: string) {
    const ret = await this.supabase
      .from('cpv_actions_complete')
      .select('*')
      .eq('claim_id', claim_id)
      //.filter('prstatus', 'not.is', null)
      //.filter('prstatus', 'not.eq', 'IN_PROGRESS')
      //.filter('type', 'neq', 'CLAIM_AGENT')
      //.order('ac_status', { ascending: false, nullsFirst: false })
    return ret.data as Action[] | null;
  }

  /**
   * NOVO MÉTODO V3 (OTIMIZADO): Busca outstanding actions usando case_actions diretamente
   * ✅ Resolve duplicatas ao trabalhar com case_actions individuais
   * ✅ Faz APENAS 3 queries essenciais (claim, actions, prerequisites)
   * ✅ NÃO busca done_actions (deixa para ManagementStepService)
   *
   * @param claim_id - ID do claim
   * @returns Lista de actions ordenadas por dependências e prioridade
   */
  async getOutstandingActionsV3(claim_id: string): Promise<EnrichedAction[]> {
    console.log('[ClaimsService V3] Starting fetch (structure only, no status)', { claim_id });

    // 1️⃣ Buscar o claim com o case_id
    const { data: claim, error: claimError } = await this.supabase
      .from('cp_claim')
      .select('id, case_id')
      .eq('id', claim_id)
      .single();

    if (claimError || !claim) {
      console.error('[ClaimsService V3] Error fetching claim:', claimError);
      return [];
    }

    console.log('[ClaimsService V3] ✅ Claim fetched', { case_id: claim.case_id });

    // 2️⃣ Buscar case_actions do case com suas actions
    const { data: caseActions, error: caseActionsError } = await this.supabase
      .from('cp_case_actions')
      .select(`
        id,
        action_id,
        case_id,
        config,
        created_at,
        cp_actions:action_id (
          id,
          name,
          type,
          action_type,
          config,
          questionnaire_id,
          contract_id
        )
      `)
      .eq('case_id', claim.case_id!);

    if (caseActionsError || !caseActions) {
      console.error('[ClaimsService V3] Error fetching case actions:', caseActionsError);
      return [];
    }

    // Filtrar actions do tipo CLAIM_AGENT
    const filteredCaseActions = (caseActions as any[]).filter(
      (ca) => ca.cp_actions?.type !== 'CLAIM_AGENT'
    ) as CaseActionWithRelations[];

    console.log('[ClaimsService V3] ✅ Case actions fetched', {
      total: filteredCaseActions.length,
      actions: filteredCaseActions.map(ca => ({
        id: ca.id,
        name: ca.cp_actions.name,
        type: ca.cp_actions.action_type,
      }))
    });

    // 3️⃣ Buscar pré-requisitos
    const caseActionIds = filteredCaseActions.map((ca) => ca.id);

    let prerequisites: ActionPrerequisite[] = [];

    if (caseActionIds.length > 0) {
      const { data: prereqsData, error: prereqsError } = await this.supabase
        .from('cp_action_prerequisites')
        .select('id, action_id, prereq_id, description')
        .in('action_id', caseActionIds)
        .in('prereq_id', caseActionIds);

      if (prereqsError) {
        console.error('[ClaimsService V3] Error fetching prerequisites:', prereqsError);
      } else {
        prerequisites = (prereqsData || []) as ActionPrerequisite[];
      }
    }

    console.log('[ClaimsService V3] ✅ Prerequisites fetched', {
      total: prerequisites.length,
      prerequisites: prerequisites.map(p => ({
        action: p.action_id,
        prereq: p.prereq_id,
      })),
    });

    // 4️⃣ Ordenar case_actions por pré-requisitos + prioridade
    const sortedCaseActionIds = sortCaseActions(
      filteredCaseActions,
      prerequisites
    );

    console.log('[ClaimsService V3] ✅ Actions sorted', {
      order: sortedCaseActionIds,
    });

    // 5️⃣ Construir EnrichedActions (SEM done_actions)
    const enrichedActions: EnrichedAction[] = sortedCaseActionIds
      .map((caseActionId) => {
        const caseAction = filteredCaseActions.find((ca) => ca.id === caseActionId);
        if (!caseAction) return null;

        const action = caseAction.cp_actions;

        // Pré-requisitos desta action
        const prereqsForAction = prerequisites.filter(
          (pr) => pr.action_id === caseActionId
        );

        // ⚠️ NÃO verifica se pré-requisitos estão completos
        // Isso será feito pelo ManagementStepService quando necessário
        const prstatus = prereqsForAction.length > 0 ? 'OPEN' : 'DONE';

        return {
          case_action_id: caseAction.id,
          action_id: action.id,
          claim_id: claim_id,
          done_action_id: null, // ⚠️ NÃO busca done_action aqui
          action_name: action.name,
          action_type: action.action_type as 'KYC' | 'QUESTIONNAIRE' | 'SIGNATURE',
          type: action.type,
          config: action.config,
          case_action_config: caseAction.config,
          questionnaire_id: action.questionnaire_id,
          contract_id: action.contract_id,
          ac_status: null, // ⚠️ NÃO busca status aqui
          prstatus: prstatus,
          status: null, // ⚠️ NÃO busca status aqui
          prerequisite_ids: prereqsForAction.map(pr => pr.prereq_id),
          description: prereqsForAction[0]?.description,
          created_at: caseAction.created_at,
        } as EnrichedAction;
      })
      .filter((action): action is EnrichedAction => action !== null);

    console.log('🔎 [WIZARD_DEBUG] ENRICHED_ACTIONS_FINAL_ORDER', {
      total: enrichedActions.length,
      order: enrichedActions.map((a, idx) => ({
        step: idx,
        name: a.action_name,
        type: a.action_type,
        caseActionId: a.case_action_id?.slice(0,8),
        prstatus: a.prstatus,
        prerequisite_ids: (a.prerequisite_ids || []).map(id => id.slice(0,8)),
      })),
    });

    return enrichedActions;
  }

  async getRequirementsListForClaimGuide(claim_id: string) {
    const ret = await this.supabase
      .from('cpv_actions_complete')
      .select('*')
      .eq('claim_id', claim_id)
      .filter('type', 'neq', 'CLAIM_AGENT')
      .order('config->priority', { ascending: false });
    return ret.data as Action[] | null;
  }

  async getActionById(actionId: string, claimId?: string): Promise<Action | null> {
    let query = this.supabase
      .from('cpv_actions_complete')
      .select('*')
      .eq('action_id', actionId);

    if (claimId) {
      query = query.eq('claim_id', claimId);
    }

    const ret = await query.limit(1);

    return (ret.data?.[0] ?? null) as Action | null;
  }

  async getActionByDoneId(actionId: string, claimId?: string): Promise<Action | null> {
    // CPK-4068: cpv_actions_complete filtered only by done_action_id can't be pushed down by the
    // planner through the cpv_actions_done UNION (~1.68M cost plan vs ~170 with claim_id resolved
    // server-side), so this reads via an RPC that resolves claim_id first when the caller doesn't
    // have it (e.g. agent manual KYC check add, where the action is picked at the case level).
    // Deploy order: "SupabaseDB - Run Migrations" before this build. Without the function PostgREST
    // answers 404 (PGRST202), ret.data is null and the KYC polling never resolves. Rollback in
    // reverse: revert this build first, then drop the function (supabase_db/MIGRATIONS.md, item 14).
    const ret = await this.supabase
      .rpc('rpc_get_action_by_done_id', { p_done_action_id: actionId, p_claim_id: claimId ?? null })
      .limit(1)
      .maybeSingle();
    return ret.data as Action | null;
  }

  override async create(
    claim: Partial<Claim>,
    userid: string | null = null,
    origination?: ClaimOrigination
  ) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const ret = (await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/create_claim',
        {
          case_id: claim.case_id,
          user_id: userid,
          origination,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    )) as any;

    return ret.message.claim;
  }

  private async checkRequirementStatus(
    r: Requirement,
    cwc: ClaimWithCase
  ): Promise<{
    status: ClaimRequirementStatus;
    data?: any;
  }> {
    switch (r.type) {
      case 'KYC':
        return { status: await this.checkPersonalDocumentRequirement(r, cwc) };

      case 'QUESTIONNAIRE':
        return { status: await this.checkQuestionnaireRequirement(r, cwc) };

      case 'SIGNATURE':
        return this.checkSignatureRequirement(r, cwc);

      default:
        return { status: ClaimRequirementStatus.UNMET };
    }

    // TODO: Implement
  }

  private async checkQuestionnaireRequirement(
    r: Requirement,
    cwc: ClaimWithCase
  ): Promise<ClaimRequirementStatus> {
    const qid = r.config.sid;

    const ur = await this.supabase
      .from('cp_questionnaire_answer')
      .select('status')
      .eq('claim_id', cwc.id)
      .eq('status', 'SUBMITTED');

    if (ur.data && ur.data.length > 0) {
      return ClaimRequirementStatus.MET;
    } else {
      return ClaimRequirementStatus.UNMET;
    }
  }

  private async checkSignatureRequirement(
    r: Requirement,
    cwc: ClaimWithCase
  ): Promise<{
    status: ClaimRequirementStatus;
    data?: { ref?: string; status?: string | null };
  }> {
    const qid = r.config.sid;

    const ur = await this.supabase
      .from('cp_signed_contract')
      .select('*')
      .eq('claim_id', cwc.id)
      .eq('contract_id', r.config.contract_id);

    if (ur.data && ur.data.length > 0) {
      return {
        status: ClaimRequirementStatus.MET,
        data: { ref: ur.data[0].id, status: ur.data[0].status },
      };
    } else {
      return { status: ClaimRequirementStatus.UNMET };
    }
  }

  private async checkPersonalDocumentRequirement(
    r: Requirement,
    cwc: ClaimWithCase
  ): Promise<ClaimRequirementStatus> {
    const ur = await this.supabase
      .from('cp_claim_permission')
      .select('principal_id')
      .eq('claim_id', cwc.id)
      .eq('permission_type', 'OWNER');

    if (!ur.data) {
      throw new Error(
        'Could not find matter`s owner, and KYC document is required.'
      );
    }

    const u = ur.data[0].principal_id!;

    const docsr = await this.supabase
      .from('cp_personal_document')
      .select('*')
      .eq('user_id', u)
      .eq('doc_type', r.config.document_title);

    console.log('docsr', docsr);

    if (!docsr.data || docsr.data.length == 0) {
      return ClaimRequirementStatus.UNMET;
    }

    const okdocs = docsr.data.map((d) => {
      if (
        d.status == 'UPLOADED' ||
        d.status == 'VALID' ||
        d.status == 'NO_VIRUS' ||
        d.status == 'CHECKING'
      )
        return true;
      else if (
        d.status == 'PRE_UPLOAD' ||
        d.status == 'REJECTED' ||
        d.status == 'VIRUS'
      ) {
        return false;
      } else {
        console.error('UNKNOWN DOCUMENT VALUE', d.status);
        return false;
      }
    });

    const some_ok = okdocs.reduce((p, curr) => p || curr, false);

    if (some_ok) {
      return ClaimRequirementStatus.MET;
    } else {
      return ClaimRequirementStatus.UNMET;
    }
  }

  async getWithDrawnClaimsRequest(user: string) {
    if (!user) {
      return [];
    }

    const { data, error } = await this.supabase
      .from('cp_withdrawal_requests')
      .select('id, status, request_date, cp_claim (id, cp_case (summary))')
      .eq('requester_id', user)
      .in('status', ['WAITING_ADMIN', 'REQUESTED']);

    if (error) {
      throw new Error(error.message);
    }

    return data as any[];
  }

  async getWithdrawalRequestsByClaimId(claimId: string) {
    const { data, error } = await this.supabase
      .from('cp_withdrawal_requests')
      .select('id, status')
      .eq('claim_id', claimId)
      .in('status', ['WAITING_ADMIN', 'REQUESTED', 'APPROVED']);

    if (error) {
      throw new Error(error.message);
    }

    return data as any[];
  }


  //USAR ESSE PARA POST DO CLIENTE
  async withdrawClaim(claimId: string, formData?: WithdrawFormData, requestAsClient: boolean = false) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    let url = environment.lambdaUrl + '/withdraw';
    if (requestAsClient) {
      url += '?requestAsClient=true';
    }

    const ret = (await firstValueFrom(
      this.http.post(
        url,
        {
          claim_id: claimId,
          form_info: formData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    )) as any;

    return ret.message.claim;
  }

  async getWithdrawalConfig(
    claimId: string,
    withdrawalType: 'cancellation' | 'termination'
  ): Promise<WithdrawalFlowConfig | null> {
    const { data: claim } = await this.supabase
      .from('cp_claim')
      .select('cp_case!inner(configuration)')
      .eq('id', claimId)
      .single();

    if (!claim) return null;

    const caseConfig = (claim.cp_case as any)?.configuration;
    const withdrawalConfig: CaseWithdrawalConfig | null | undefined = caseConfig?.withdrawal;

    if (withdrawalConfig == null) return null;

    const flowConfig = withdrawalType === 'cancellation'
      ? withdrawalConfig.cancellation
      : withdrawalConfig.termination;

    return flowConfig ?? null;
  }

  /**
   * Determines which portal message to send based on withdrawal type and timing.
   * If a config is provided and has post_submission.message, that takes precedence
   * over the legacy message lookup.
   */
  getWithdrawalPortalMessage(
    claim: ClaimWithCase,
    contractSignedDate: Date,
    withdrawalType: 'cancellation' | 'termination',
    withdrawalRequestDate?: Date,
    config?: WithdrawalFlowConfig | null
  ): string {
    const lang = this.ts.currentLang?.startsWith('pt') ? 'pt' : 'en';

    // Config-driven: use withdraw_confirmation.message if available
    if (config?.withdraw_confirmation?.message) {
      return config.withdraw_confirmation.message[lang as 'en' | 'pt']
        || config.withdraw_confirmation.message.en;
    }

    const SERASA_CASE_SLUG = 'caso-megavazamento-de-dados';

    if (claim.cp_case?.slug !== SERASA_CASE_SLUG) {
      return this.ts.instant(
        withdrawalType === 'cancellation'
          ? 'CancellationRequestedMessage'
          : 'TerminationRequestedMessage'
      );
    }

    const actionFiledDateStr = claim.cp_case?.attributes?.['action']?.['action_filed_at'] as string | undefined;
    const actionFiled = actionFiledDateStr ? this.parseDate(actionFiledDateStr) : null;

    // Check if withdrawal request was made before or after action was filed
    let isRequestBeforeActionFiled = true; // Default to true if no dates
    if (withdrawalRequestDate && actionFiled) {
      isRequestBeforeActionFiled = withdrawalRequestDate < actionFiled;
    } else if (!actionFiled) {
      isRequestBeforeActionFiled = true;
    }

    if (withdrawalType === 'cancellation') {
      console.log('[ClaimsService] getWithdrawalPortalMessage - Cancellation', {
        actionFiledDateStr,
        contractSignedDate: contractSignedDate.toISOString(),
        withdrawalRequestDate: withdrawalRequestDate?.toISOString(),
      });

      // Determine if user signed before or after action was filed
      let signedBeforeActionFiled = true; // Default to true if no action filed date
      if (actionFiled) {
        signedBeforeActionFiled = contractSignedDate < actionFiled;
      }

      // Check if contract was signed between 9/jan and 12/jan (any year)
      const contractMonth = contractSignedDate.getMonth(); // 0-indexed (0 = January)
      const contractDay = contractSignedDate.getDate();
      const isContractSignedInSpecialPeriod =
        contractMonth === 0 && contractDay >= 9 && contractDay <= 12;

      console.log('[ClaimsService] getWithdrawalPortalMessage - Cancellation - decision factors', {
        isRequestBeforeActionFiled,
        signedBeforeActionFiled,
        isContractSignedInSpecialPeriod,
      });

      // Rule 1: Request cancellation BEFORE action is filed AND signed BEFORE action is filed
      if (isRequestBeforeActionFiled && signedBeforeActionFiled) {
        return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_49');
      }

      // Rule 2: Request cancellation AFTER action is filed AND signed BEFORE action is filed
      if (!isRequestBeforeActionFiled && signedBeforeActionFiled) {
        // Exception: Contract signed between 9/jan and 12/jan
        if (isContractSignedInSpecialPeriod) {
          return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_54');
        }
        return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_55');
      }

      // Rule 3: Request cancellation AFTER action is filed AND signed AFTER action is filed
      if (!isRequestBeforeActionFiled && !signedBeforeActionFiled) {
        // Exception: Contract signed between 9/jan and 12/jan
        if (isContractSignedInSpecialPeriod) {
          return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_54');
        }
        return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_51');
      }

      // Fallback (should not reach here)
      return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_49');
    }

    // Termination logic
    if (withdrawalType === 'termination') {
      console.log('[ClaimsService] getWithdrawalPortalMessage - Termination', {
        actionFiledDateStr,
        contractSignedDate: contractSignedDate.toISOString(),
        withdrawalRequestDate: withdrawalRequestDate?.toISOString(),
        isRequestBeforeActionFiled,
      });

      // Rule 1: Requests termination BEFORE action is filed (regardless of terminate and withdraw or terminate only)
      if (isRequestBeforeActionFiled) {
        return this.ts.instant('WITHDRAWAL.TERMINATION.MESSAGE_59');
      }

      // Rule 2: Requests termination AFTER action is filed (regardless of terminate and withdraw or terminate only)
      return this.ts.instant('WITHDRAWAL.TERMINATION.MESSAGE_57');
    }

    // Fallback
    return this.ts.instant('WITHDRAWAL.CANCELLATION.MESSAGE_49');
  }

  /**
   * Parses date string in DD/MM/YYYY or YYYY-MM-DD format
   */
  private parseDate(dateStr: string): Date | null {
    try {
      // Try YYYY-MM-DD format first (ISO format)
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const day = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
      }

      // Try DD/MM/YYYY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          return new Date(year, month, day);
        }
      }
    } catch (e) {
      console.error('Error parsing date:', dateStr, e);
    }
    return null;
  }


  async manageWithdrawClaim(wc: string, status: 'APPROVE' | 'REJECT') {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const ret = (await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/withdraw',
        {
          withdraw_id: wc,
          status: status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    )) as any;

    return ret;
  }

  /**
   *
   * create table
  public.cp_claim_permission (
    id uuid not null default gen_random_uuid (),
    created_at timestamp with time zone null default now(),
    valid_from timestamp with time zone null,
    valid_until timestamp with time zone null,
    permission_type text null,
    principal_id uuid null,
    claim_id uuid null,
    constraint cp_claim_permission_pkey primary key (id),
    constraint cp_claim_permission_claim_id_fkey foreign key (claim_id) references cp_claim (id) on delete cascade,
    constraint cp_claim_permission_principal_id_fkey foreign key (principal_id) references cp_user (id) on delete cascade
  ) tablespace pg_default;
   */
  async getRepresentativesForClaim(claimId: string) {
    const query = this.supabase
      .from('cp_claim_permission')
      .select(
        'id, principal_id, permission_type, cp_user(id, display_name, cp_pii(id_document_value))'
      )
      .eq('claim_id', claimId);
    // .eq('permission_type', 'REPRESENTATIVE');
    const { data, error } = await query;

    if (error) throw new Error(error.message);

    return data as unknown as string[];
  }

  async addPermission(claim: ClaimWithCase, user_id: string, type: string) {
    return await this.supabase.from('cp_claim_permission').insert({
      claim_id: claim.id,
      permission_type: type,
      principal_id: user_id,
    });
  }

  async getChangeLogByClaimPaged(
    claimId: string,
    page: number = 0,
    pageSize: number = 5
  ) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    // cpv_change_logs_by_claim has no PostgREST grant anymore: it ran with
    // security_invoker off, and its last UNION branch exposes every audited row
    // in the platform (cp_pii, cp_user, cp_kyc_check...) once the claim_id filter
    // is dropped. change_logs_by_claim_list is ADMINISTRATOR-only and always
    // applies claim_id, matching cp_change_logs' single RLS policy.
    const res = await this.callLambda('change_logs_by_claim_list', 'POST', {
      claimId,
      page,
      pageSize,
    });

    const body = res?.details ?? res ?? {};
    const data = (body.data ?? []) as ChangeLogByClaim[];
    const count = body.count ?? 0;
    const totalPages = count ? Math.ceil(count / pageSize) : 0;

    return {
      data: data as ChangeLogByClaim[],
      page,
      pageSize,
      from,
      to,
      count,
      totalPages,
    };
  }

  async getActionsClaimDetails(claimId: string) {
    let { data, error } = await this.supabase
      .from('cpv_actions_claimdetails')
      .select('*')
      .eq('owner_claim_id', claimId);

    if (error) throw new Error(error.message);

    return data;
  }

  async createClaimForUser(caseId: string, userId: string) {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const ret = await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/create_claim',
        {
          case_id: caseId,
          user_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );

    return ret as any;
  }

  async updateClaimStatus(
    claimId: string,
    newStatus: 'ACTIVE' | 'INACTIVE',
    manualChange: {
      reasons: string[];
      notes?: string | null;
    }
  ) {
    return this.callLambda('update-claim-status', 'PUT', {
      claim_id: claimId,
      status: newStatus,
      reasons: manualChange.reasons,
      notes: manualChange.notes ?? undefined,
    });
  }

  async getCaseById(caseId: string) {
    return await this.supabase
      .from('cp_case')
      .select('*')
      .eq('id', caseId)
      .single();
  }

  async getConsentAnswer(claimId: string, questionSlug: string): Promise<boolean> {
    const { data } = await this.supabase
      .from('cp_questionnaire_answer')
      .select('answers')
      .eq('claim_id', claimId)
      .in('status', ['SUBMITTED', 'COMPLETED']);

    if (!data?.length) return false;

    return data.some((row) => {
      const answers = (row as { answers?: Record<string, unknown> }).answers;
      if (!answers || !(questionSlug in answers)) return false;
      const value = getValue({ answers }, `answers.${questionSlug}`);
      return value === true || value === 'true';
    });
  }
}

export interface IssuedClaimRow {
  id: string;
  status: string | null;
  result: { status?: string | null } | null;
  issued_date: string | null;
  created_at: string;
  claim_form_name: string | null;
}

export interface ClaimWithCase {
  id: string;
  name: string;
  status: string;
  nickname: string;
  slug: string;
  case_id: string;
  owner_client_id: string;
  is_private_access: boolean;
  description: string;
  created_at: '2023-04-12';
  updated_at: null;
  attributes: any;
  cp_case: Case;
  cp_claim_permission: ClaimPermission[];
}

export interface ClaimRequirement {
  requirement: Requirement;
  status: ClaimRequirementStatus;
  claimId: string;
  data?: any;
}

export enum ClaimRequirementStatus {
  'MET',
  'UNMET',
  'DEADLINE_MISSED',
}

export type CaseConfiguration = {
  images: {
    high_res: string;
  };
  requirements: [];
  enable_actions_wizard?: boolean;
};

export interface ChangeLogByClaim {
  id: string; // uuid
  table_name: string;
  operation_type: string;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  change_time: string; // ISO date string
  user_id: string | null; // uuid ou null
  claim_id: string | null; // uuid ou null
}

export interface ClaimPermission {
  id: string;
  principal_id: string;
  claim_id: string;
  permission_type: string;
}
